import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { linkListStyles } from "@insite/content-library/Widgets/Basic/LinkList";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    containerLinksGridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    newsLetterGridItem?: GridItemProps;
    infoCenterGridItem?: GridItemProps;
    customerSupportGridItem?: GridItemProps;
    followUsGridItem?: GridItemProps;
    copyRightsGridItem?: GridItemProps;
    companyInfoGridItem?: GridItemProps;
    copyRightsLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            margin: 0 auto;
            padding: 0rem 1.5rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 !important;
                    `,
                    css`
                        padding: 0 !important;
                    `,
                    null,
                    null,
                    null,
                ])}

            > ${GridOffset} {
                width: 100%;
                margin: 0 auto;
            }
        `,
    },
    logoGridItem: {
        width: 12,
        css: css`
            padding-top: 40px;
            padding-bottom: 40px;
            width: 100%;
            display: flex;
            justify-content: center;
            background: ${getColor("primary")};
        `,
    },
    containerLinksGridItem: {
        width: 12,
        align: "top",
        css: css`
            padding: 2rem 2.5rem 0 !important;
        `,
    },
    newsLetterGridItem: {
        width: 12,
        css: css`
            padding: 0px;
        `,
    },
    companyInfoGridItem: {
        width: [12, 12, 6, 3, 3],
    },
    infoCenterGridItem: {
        width: [12, 12, 6, 3, 3],
    },
    customerSupportGridItem: {
        width: [12, 12, 6, 3, 3],
    },
    followUsGridItem: {
        width: [12, 12, 6, 3, 3],
    },
    copyRightsGridItem: {
        width: 12,
        css: css`
            padding: 0 15px;
        `,
    },

    copyRightsLinksGridItem: {
        width: 12,
        css: css`
            order: 4;
            background-color: ${getColor("primary")};
            ${GridWrapper} {
                margin: 0 auto;
                padding: 0.5rem 2.5rem;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0.5rem 1.5rem;
                        `,
                        css`
                            padding: 0.5rem 1.5rem;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            ${GridOffset} {
                align-items: center;
            }
        `,
    },
    siteMapLinksGridItem: {
        width: 12,
        css: css`
            padding: 0;
            > div[class^="Wrapper"] {
                > ${GridWrapper} {
                    > ${GridOffset} {
                        width: calc(100% - 0px);
                        margin: 0px;
                    }
                }
            }
        `,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            padding-top: 40px;
            padding-bottom: 40px;
            width: 100%;
            display: flex;
            justify-content: center;
            background: ${getColor("primary")};
            > div[class^="Wrapper"] {
                > ${GridWrapper} {
                    justify-content: center;
                    margin: 0 auto;
                }
            }
        `,
    },
};

linkListStyles.linkWrapper = {
    css: css`
        > a {
            font-size: 14px;
            > span {
                color: ${getColor("common.background")} !important;
                text-transform: capitalize;
            }
        }
    `,
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <>
            <GridContainer {...styles.widgetGridContainer}>
                <GridItem {...styles.newsLetterGridItem}>
                    <Zone zoneName="NewsLetter" contentId={id} />
                </GridItem>
                <GridItem {...styles.companyInfoGridItem}>
                    <Zone zoneName="CompanyInfo" contentId={id} />
                </GridItem>
                <GridItem {...styles.infoCenterGridItem}>
                    <Zone zoneName="InfoCenter" contentId={id} />
                </GridItem>
                <GridItem {...styles.customerSupportGridItem}>
                    <Zone zoneName="CustomerSupport" contentId={id} />
                </GridItem>
                <GridItem {...styles.followUsGridItem}>
                    <Zone zoneName="FollowUs" contentId={id} />
                </GridItem>
                <GridItem {...styles.copyRightsGridItem}>
                    <Zone zoneName="CopyRight" contentId={id} />
                </GridItem>
            </GridContainer>
            {/* <GridItem {...styles.copyRightsLinksGridItem}>
                <Zone zoneName="CopyRights" contentId={id} />
            </GridItem> */}
        </>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
